import { useCallback } from 'react';
import {
  FILE_ORDERING_TYPE,
  FILES_LIST_TYPE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  TASK_LIST_TYPE
} from '../../global/constants';
import { isEmpty } from '../../helpers/common';
import {
  batchUpdatePreferences,
  getWorkspaceProjectPreferenceData,
  WorkspacePreferenceUpdate
} from '../../helpers/firebaseHelper';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { ROUTES } from '../../routes/urls';
import { getCurrentProjectDetails } from '../../services/projectServices';
import { AccountingProps, CurrentProject, CurrentWorkspace } from '../../interfaces/ProjectInterface';

interface UseAccountingProjectLoaderProps {
  currentProject: CurrentProject;
  currentWorkspace: CurrentWorkspace;
  history: { push: (path: string) => void };
  params: { id: string };
  props: AccountingProps;
  setFilesViewListType: (listType: number) => void;
}

export const useAccountingProjectLoader = ({
  currentProject,
  currentWorkspace,
  history,
  params,
  props,
  setFilesViewListType
}: UseAccountingProjectLoaderProps) => {
  const loadData = useCallback(
    async (force = false) => {
      if (!isEmpty(currentWorkspace?.id) && (params.id !== currentProject?.id || force) && !props.isProjectTasks) {
        const projectDetails = await getCurrentProjectDetails(params.id);
        const activeUser = UserPreferenceSingleton.getInstance().getCurrentUser();
        if (!projectDetails?.users?.find((user) => user?.id === activeUser?.id)) {
          history.push(ROUTES.INBOX);
          return;
        }
      }
      const projectFilter = (await getWorkspaceProjectPreferenceData(params.id, 'tasks_filter_type')) || {};
      // Collect every preference change this load needs and commit them as one batched write at the
      // end, instead of firing up to three sequential setDoc() round-trips against the same doc.
      const preferenceUpdates: WorkspacePreferenceUpdate[] = [];
      if (currentProject?.id && !currentProject?.priority?.enabled && projectFilter?.order === SHOW_TASK_BY_ORDER.PRIORITY) {
        preferenceUpdates.push({
          itemType: 'projects',
          itemId: params.id,
          data: { tasks_filter_type: { ...projectFilter, order: SHOW_TASK_BY_ORDER.DUE_DATE } }
        });
      }
      const grouping = currentProject?.statusEnable
        ? SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS
        : currentProject?.isGroupEnabled
        ? SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP
        : SHOW_TASK_GROUPING_TYPE.DEFAULT;
      const order =
        projectFilter?.grouping === SHOW_TASK_GROUPING_TYPE.DEFAULT
          ? SHOW_TASK_BY_ORDER.CUSTOM
          : projectFilter?.order === SHOW_TASK_BY_ORDER.CUSTOM
          ? SHOW_TASK_BY_ORDER.CREATED_DATE
          : projectFilter?.order || SHOW_TASK_BY_ORDER.CREATED_DATE;
      const invalidGrouping =
        (projectFilter?.grouping === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE && !currentProject?.isMilestone) ||
        (projectFilter?.grouping === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS &&
          (!currentProject?.statusEnable || currentProject?.statusData?.length === 0)) ||
        (projectFilter?.grouping === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY && !currentProject?.priority?.enabled) ||
        (projectFilter?.grouping === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP && !currentProject?.isGroupEnabled);
      if (currentProject?.id && invalidGrouping) {
        preferenceUpdates.push({
          itemType: 'projects',
          itemId: params.id,
          data: { tasks_filter_type: { ...projectFilter, order, grouping } }
        });
      }
      const orderWithView =
        projectFilter?.viewType === TASK_LIST_TYPE.CALENDAR_VIEW &&
        (projectFilter?.order === SHOW_TASK_BY_ORDER.DUE_DATE || !projectFilter?.order)
          ? SHOW_TASK_BY_ORDER.PRIORITY
          : projectFilter?.order || projectFilter?.order === 0
          ? projectFilter.order
          : projectFilter?.viewType === TASK_LIST_TYPE.CALENDAR_VIEW
          ? SHOW_TASK_BY_ORDER.PRIORITY
          : SHOW_TASK_BY_ORDER.DUE_DATE;
      setFilesViewListType((projectFilter?.files || FILES_LIST_TYPE.GRID) as number);
      currentProject?.setFileOrdering(projectFilter?.fileOrdering || FILE_ORDERING_TYPE.DATE_UPLOADED);
      if (orderWithView !== projectFilter?.order) {
        preferenceUpdates.push({
          itemType: 'projects',
          itemId: params.id,
          data: { tasks_filter_type: { ...projectFilter, order: orderWithView } }
        });
      }
      if (preferenceUpdates.length) await batchUpdatePreferences(preferenceUpdates);
    },
    [currentProject, currentWorkspace?.id, history, params.id, props.isProjectTasks, setFilesViewListType]
  );

  return loadData;
};
